import { createLightTheme, BrandVariants, Theme } from '@fluentui/react-components';

/**
 * Harleys brand variants — red ramp built off #E3272D primary, #6F0F12 deep.
 * Fluent v9 uses the 90 stop as the primary; lighter stops fade toward red
 * soft (#FCE8E9), darker stops fade through wine to near-black.
 *
 * The crucial change from v1.2.0: NO BLUE anywhere. Brand is red+black only.
 */
const harleysBrand: BrandVariants = {
  10:  '#1A0306',
  20:  '#34060B',
  30:  '#4F0A11',
  40:  '#6F0F12', // Red Dark
  50:  '#85131A',
  60:  '#99171B', // Red Active
  70:  '#AC1B1F',
  80:  '#C21F24', // Red Hover
  90:  '#c92b36', // PRIMARY
  100: '#E94349',
  110: '#EF6065',
  120: '#F15A5F', // Red Light
  130: '#F58B8E',
  140: '#FAB6B8',
  150: '#FCE8E9', // Red Soft
  160: '#FEF6F6',
};

const baseLight: Theme = createLightTheme(harleysBrand);

/**
 * Override neutrals to use the Harleys black/grey scale instead of Fluent's
 * default cool greys. Effect: backgrounds and borders sit in a warmer,
 * more industrial tone that matches the brand.
 */
export const harleysTheme: Theme = {
  ...baseLight,
  colorBrandBackground: '#c92b36',
  colorBrandBackgroundHover: '#b32430',
  colorBrandBackgroundPressed: '#9f202b',
  colorBrandForeground1: '#b32430',
  colorBrandForeground2: '#9f202b',

  // Surfaces
  colorNeutralBackground1: '#FFFFFF',
  colorNeutralBackground2: '#F8FAFC',
  colorNeutralBackground3: '#EEF1F5',
  colorNeutralBackground4: '#E3E8EF',
  colorNeutralBackground5: '#DFE4EB',
  colorNeutralBackground6: '#C5CCD6',

  // Text
  colorNeutralForeground1: '#202127',
  colorNeutralForeground2: '#30343A',
  colorNeutralForeground3: '#686c78',
  colorNeutralForeground4: '#686c78',

  // Borders
  colorNeutralStroke1: '#DFE4EB',
  colorNeutralStroke2: '#E8EDF3',
  colorNeutralStroke3: '#F3F6FA',

  // Status — kept on-brand: success green, warning orange, error = brand red
  colorStatusDangerForeground1: '#c92b36',
  colorStatusDangerBackground1: '#FCE8E9',
  colorStatusDangerBorder1:    '#F15A5F',
  colorStatusSuccessForeground1: '#2E7D32',
  colorStatusSuccessBackground1: '#E8F5E9',
  colorStatusWarningForeground1: '#8a5200',
  colorStatusWarningBackground1: '#FFF3E0',

  // Brand foreground
  colorBrandForegroundLink: '#C21F24',
  colorBrandForegroundLinkHover: '#99171B',

  fontFamilyBase: "'Source Sans 3', 'Segoe UI', sans-serif",

  borderRadiusSmall: '10px',
  borderRadiusMedium: '8px',
  borderRadiusLarge: '16px',
  borderRadiusXLarge: '20px',
  fontSizeBase300: '14px',
  fontSizeBase400: '16px',
  fontSizeBase500: '18px',
  fontSizeBase600: '20px',
  fontSizeHero700: '28px',
  fontSizeHero800: '32px',
  lineHeightBase400: '22px',
  lineHeightBase500: '24px',
  shadow4: '0 8px 18px rgba(17,24,36,0.08)',
  shadow8: '0 12px 28px rgba(17,24,36,0.1)',
  shadow16: '0 18px 44px rgba(17,24,36,0.12)',
};

/**
 * Phase colours — STOP red, THINK orange, ACT green, REVIEW black.
 * Removed the original blue from REVIEW (replaced with brand black).
 */
export const phaseColors = {
  STOP: '#c92b36',
  THINK: '#8a5200',
  ACT: '#2E7D32',
  REVIEW: '#231F20',
} as const;

export const riskColors: Record<string, string> = {
  Low: '#2E7D32',
  Medium: '#8a5200',
  High: '#6F0F12',
  Extreme: '#c92b36',
};

/**
 * Mobile-first font tokens. iOS will zoom on input focus if font-size < 16px,
 * so we set inputs at 16px minimum. Body text 14px; meta/labels 12px.
 */
export const fontSizes = {
  /** Inputs MUST use this on mobile to suppress iOS zoom-on-focus */
  input: '16px',
  body: '15px',
  label: '14px',
  meta: '13px',
  small: '12px',
};

/** Tap target size — iOS HIG minimum */
export const TAP_SIZE = '44px';
